<template>
  <div class="p-6 mx-auto max-w-7xl space-y-5">
    <!-- Header -->
    <div class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div class="space-y-1">
        <h1 class="text-2xl font-bold leading-tight">Аналитика стационара</h1>
      </div>

      <button
        class="inline-flex items-center gap-2 px-4 py-2 rounded-xl border hover:bg-gray-50"
        @click="refreshDashboard"
        :disabled="loading"
      >
        <svg v-if="loading" class="animate-spin h-4 w-4" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
        </svg>
        <span>Обновить</span>
      </button>
    </div>

    <!-- Фильтры -->
    <div class="border rounded-2xl p-5 bg-white space-y-4">
      <!-- Верхний ряд: клиника + кнопки периодов -->
      <div class="grid gap-4 lg:grid-cols-[280px_1fr] lg:items-end">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium uppercase tracking-wide text-gray-500">Клиника</span>
          <select v-model="clinicId" class="w-full rounded-xl border px-4 py-3 text-sm bg-white">
            <option value="">Все клиники</option>
            <option v-for="c in clinicOptions" :key="c.id" :value="String(c.id)">{{ c.name }}</option>
          </select>
        </div>

        <div class="flex flex-wrap gap-2 items-center lg:justify-start">
          <button
            v-for="p in periodButtons"
            :key="p.value"
            class="min-h-12 px-4 py-3 rounded-xl text-sm border transition-colors"
            :class="period === p.value && !showCustomDates ? 'bg-black text-white border-black' : 'bg-white text-gray-700 hover:bg-gray-50'"
            @click="setPeriod(p.value)"
          >{{ p.label }}</button>
          <button
            class="min-h-12 px-4 py-3 rounded-xl text-sm border transition-colors"
            :class="showCustomDates ? 'bg-black text-white border-black' : 'bg-white text-gray-700 hover:bg-gray-50'"
            @click="showCustomDates = !showCustomDates; if(showCustomDates) period = ''"
          >Даты</button>
          <button
            class="min-h-12 px-4 py-3 rounded-xl text-sm border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
            :disabled="exporting"
            :title="exportError || 'Скачать историю выбранной клиники в JSON'"
            @click="exportHistory"
          >{{ exporting ? 'Готовлю…' : 'Выгрузить JSON' }}</button>
        </div>
      </div>

      <!-- Нижний ряд: произвольные даты (если открыто) -->
      <div v-if="showCustomDates" class="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end">
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">От</span>
          <input type="date" v-model="dateFrom" class="rounded-xl border px-3 py-3 text-sm bg-white" />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">До</span>
          <input type="date" v-model="dateTo" class="rounded-xl border px-3 py-3 text-sm bg-white" />
        </div>
        <button class="px-4 py-3 rounded-xl bg-black text-white text-sm h-12" @click="fetchData" :disabled="loading">Применить</button>
      </div>
    </div>

    <!-- Ошибка -->
    <div v-if="error" class="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      Ошибка: {{ error }}
    </div>

    <!-- Статы -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Stat label="Всего эпикризов" :value="data?.total_epicrises ?? (loading ? '—' : 0)" />
      <Stat label="По тексту" :value="data?.total_by_text ?? 0" />
      <Stat label="По файлу" :value="data?.total_by_file ?? 0" />
      <Stat label="Клиник в выборке" :value="clinicRows.length" />
    </div>

    <!-- Табы -->
    <div>
      <div class="inline-flex rounded-xl border bg-white p-1">
        <button :class="tabBtn('clinics')" @click="activeTab = 'clinics'">По клиникам</button>
        <button :class="tabBtn('doctors')" @click="activeTab = 'doctors'">Врачи</button>
        <button :class="tabBtn('types')" @click="activeTab = 'types'">Типы запросов</button>
      </div>

      <!-- ========== По клиникам ========== -->
      <div v-if="activeTab === 'clinics'" class="mt-4 space-y-4">
        <div class="rounded-2xl border bg-white p-4">
          <h3 class="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <span class="w-2 h-2 bg-blue-500 rounded-full"></span>
            Распределение по клиникам
          </h3>
          <div class="h-80">
            <VChart v-if="clinicRows.length" :option="simpleClinicChartOption" autoresize />
            <div v-else class="h-full flex items-center justify-center text-slate-300 text-sm italic border-2 border-dashed border-slate-50 rounded-2xl">
              Нет данных за период
            </div>
          </div>
        </div>

        <div class="overflow-auto rounded-2xl border bg-white">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="text-left p-3">Клиника</th>
                <th class="text-right p-3">Врачей</th>
                <th class="text-right p-3">По тексту</th>
                <th class="text-right p-3">По файлу</th>
                <th class="text-right p-3">Итого</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in clinicRows"
                :key="idx"
                class="border-t hover:bg-gray-50 cursor-pointer"
                @click="handleClinicClick(row)"
                title="Открыть врачей клиники"
              >
                <td class="p-3">{{ row.name }}</td>
                <td class="p-3 text-right">{{ row.doctor_count }}</td>
                <td class="p-3 text-right">{{ row.by_text }}</td>
                <td class="p-3 text-right">{{ row.by_file }}</td>
                <td class="p-3 text-right font-medium">{{ row.total }}</td>
              </tr>
              <tr v-if="clinicRows.length === 0">
                <td class="p-3 text-gray-500" colspan="5">Нет данных</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ========== Врачи ========== -->
      <div v-if="activeTab === 'doctors'" class="mt-4 space-y-4">
        <!-- Гист топ-15 -->
        <div class="rounded-2xl border bg-white p-4">
          <div class="h-80">
            <VChart :option="doctorChartOption" autoresize />
          </div>
        </div>

        <!-- Поиск/сорт -->
        <div class="rounded-2xl border bg-white p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div class="flex items-center gap-2">
            <input
              v-model.trim="doctorSearch"
              type="text"
              class="rounded-xl border px-3 py-2 w-72"
              placeholder="Поиск врача…"
            />
            <button class="px-3 py-2 rounded-xl border" @click="doctorSearch = ''">Сброс</button>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">Сортировка:</span>
            <select v-model="doctorSortBy" class="rounded-xl border px-3 py-2">
              <option value="total_epicrises">Итого</option>
              <option value="by_text">По тексту</option>
              <option value="by_file">По файлу</option>
              <option value="last_activity">Последняя активность</option>
              <option value="doctor_name">ФИО</option>
            </select>
            <button class="px-3 py-2 rounded-xl border" @click="doctorSortDesc = !doctorSortDesc">
              {{ doctorSortDesc ? '↓ убыв.' : '↑ возр.' }}
            </button>
          </div>
        </div>

        <!-- Таблица -->
        <div class="overflow-auto rounded-2xl border bg-white">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="text-left p-3">Врач</th>
                <th class="text-left p-3">Клиника</th>
                <th class="text-right p-3">По тексту</th>
                <th class="text-right p-3">По файлу</th>
                <th class="text-right p-3">Последняя активность</th>
                <th class="text-right p-3">Итого</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in doctorsPaged"
                :key="d.doctor_id"
                class="border-t hover:bg-gray-50 cursor-pointer"
                @click="openDoctorDetail(d)"
                title="Подробнее о враче"
              >
                <td class="p-3">{{ d.doctor_name }}</td>
                <td class="p-3">{{ d._clinic_name || '—' }}</td>
                <td class="p-3 text-right">{{ d.by_text }}</td>
                <td class="p-3 text-right">{{ d.by_file }}</td>
                <td class="p-3 text-right">{{ fmtDate(d.last_activity) }}</td>
                <td class="p-3 text-right font-medium">{{ d.total_epicrises }}</td>
              </tr>
              <tr v-if="doctorsAll.length === 0">
                <td class="p-3 text-gray-500" colspan="6">Нет данных</td>
              </tr>
            </tbody>
          </table>

          <!-- Пагинация -->
          <div v-if="doctorsAll.length > 0" class="flex items-center justify-between p-3 text-sm border-t">
            <div>Показано {{ Math.min(doctorPageEnd, doctorsAll.length) }} из {{ doctorsAll.length }}</div>
            <div class="flex items-center gap-2">
              <button class="px-3 py-1 rounded border" :disabled="doctorPage === 1" @click="doctorPage--">Назад</button>
              <span>Стр. {{ doctorPage }} / {{ doctorTotalPages }}</span>
              <button class="px-3 py-1 rounded border" :disabled="doctorPage >= doctorTotalPages" @click="doctorPage++">Вперёд</button>
              <select v-model.number="doctorPageSize" class="rounded border px-2 py-1">
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== Типы запросов ========== -->
      <div v-if="activeTab === 'types'" class="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="rounded-2xl border bg-white p-4">
          <div class="h-72">
            <VChart :option="typeChartOption" autoresize />
          </div>
        </div>

        <div class="rounded-2xl border bg-white p-4 space-y-2 text-sm text-gray-700">
          <h3 class="font-semibold">Диапазон</h3>
          <div>
            <template v-if="data?.period">
              От: <b>{{ data.period.from ?? '—' }}</b> до <b>{{ data.period.to ?? '—' }}</b>
            </template>
            <template v-else>—</template>
          </div>
          <div>Фильтр клиники: <b>{{ selectedClinicName || 'все' }}</b></div>
          <div>Всего эпикризов: <b>{{ data?.total_epicrises ?? 0 }}</b></div>
        </div>
      </div>
    </div>

    <!-- ========== МОДАЛЬНОЕ ОКНО ВРАЧА ========== -->
    <div
      v-if="doctorDetail"
      class="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/40"
      @click.self="doctorDetail = null"
    >
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[70vh] overflow-auto p-6 space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-bold">{{ doctorDetail.doctor_name }}</h2>
          <button class="text-gray-400 hover:text-gray-700 text-xl" @click="doctorDetail = null">&times;</button>
        </div>

        <div v-if="doctorDetailLoading" class="py-8 text-center text-gray-500">Загрузка…</div>

        <template v-else>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Эпикризов" :value="doctorDetail.total_epicrises ?? 0" />
            <Stat label="По тексту" :value="doctorDetail.by_text ?? 0" />
            <Stat label="По файлу" :value="doctorDetail.by_file ?? 0" />
            <Stat label="Соответствие" :value="complianceLabel" />
          </div>

          <div v-if="doctorDetail.by_mkb?.length">
            <h3 class="text-sm font-semibold text-gray-600 mb-2">МКБ-коды</h3>
            <div class="overflow-auto rounded-xl border">
              <table class="min-w-full text-sm">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="text-left p-2">Код</th>
                    <th class="text-left p-2">Диагноз</th>
                    <th class="text-right p-2">Кол-во</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in doctorDetail.by_mkb" :key="m.mkb_code" class="border-t">
                    <td class="p-2 font-mono">{{ m.mkb_code }}</td>
                    <td class="p-2 text-gray-700 max-w-xs truncate" :title="m.diagnosis">{{ m.diagnosis }}</td>
                    <td class="p-2 text-right">{{ m.count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="doctorDetail.recent_epicrises?.length">
            <h3 class="text-sm font-semibold text-gray-600 mb-2">Последние эпикризы</h3>
            <div class="overflow-auto rounded-xl border">
              <table class="min-w-full text-sm">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="text-left p-2">ID</th>
                    <th class="text-left p-2">МКБ</th>
                    <th class="text-left p-2">Соответствие</th>
                    <th class="text-left p-2">Дата</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="e in doctorDetail.recent_epicrises" :key="e.id" class="border-t">
                    <td class="p-2">{{ e.id }}</td>
                    <td class="p-2 font-mono">{{ e.mkb_code }}</td>
                    <td class="p-2">{{ e.compliance_rate }}</td>
                    <td class="p-2">{{ fmtDate(e.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="doctorDetail.by_day?.length" class="rounded-xl border p-3">
            <div class="h-40">
              <VChart :option="doctorDetailChart" autoresize />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, shallowRef } from 'vue'
import { stacionarService } from '@/services/stacionarService'

/* ECharts */
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, PieChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from 'echarts/components'
import VChart from 'vue-echarts'
use([CanvasRenderer, BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent])

/* Stat card — exact same as AdminAnalytics */
const Stat = {
  props: { label: String, value: [String, Number] },
  template: `
    <div class="rounded-2xl bg-white shadow-sm border p-4 flex flex-col gap-1">
      <span class="text-sm text-gray-500">{{ label }}</span>
      <span class="text-2xl font-semibold">{{ value }}</span>
    </div>
  `,
}

/* ========== state ========== */
const loading = ref(false)
const error = ref(null)
const data = shallowRef(null)
const allEpicrises = shallowRef([])

/* filters */
const clinicId = ref('')
const period = ref('this_month')
const dateFrom = ref('')
const dateTo = ref('')
const showCustomDates = ref(false)

const periodButtons = [
  { value: 'today', label: 'Сегодня' },
  { value: 'last_7_days', label: '7 дней' },
  { value: 'this_month', label: 'Месяц' },
]

const DASHBOARD_CACHE_TTL = 3 * 60 * 1000
const EPICRISIS_CACHE_TTL = 20 * 60 * 1000
const epicrisisPageSize = 500
const epicrisisMaxPages = 200
const epicrisisCacheKey = 'stacionar:all-epicrises:v2'

function extractEpicrisisItems(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

function safeStorageGet(key) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function safeStorageSet(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* storage can be unavailable or full */
  }
}

function readCachedValue(key, ttlMs) {
  const raw = safeStorageGet(key)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null

    const savedAt = Number(parsed.savedAt ?? 0)
    const age = Date.now() - savedAt
    return {
      value: parsed.value,
      fresh: Number.isFinite(age) && age >= 0 && age < ttlMs,
    }
  } catch {
    return null
  }
}

function writeCachedValue(key, value) {
  safeStorageSet(key, JSON.stringify({ savedAt: Date.now(), value }))
}

function normalizeParams(params) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .reduce((acc, [key, value]) => {
      acc[key] = value
      return acc
    }, {})
}

function makeCacheKey(prefix, params = {}) {
  return `${prefix}:${JSON.stringify(normalizeParams(params))}`
}

function resolveLastPage(payload, pageSize, itemCount) {
  const meta = payload?.meta ?? payload?.pagination ?? payload
  const lastPage = Number(meta?.last_page ?? meta?.lastPage ?? meta?.total_pages)
  if (Number.isFinite(lastPage) && lastPage > 0) return lastPage

  const total = Number(meta?.total ?? meta?.count ?? meta?.total_items)
  if (Number.isFinite(total) && total > 0) return Math.max(1, Math.ceil(total / pageSize))

  if (itemCount < pageSize) return 1
  return null
}

function setPeriod(val) {
  period.value = val
  showCustomDates.value = false
  dateFrom.value = ''
  dateTo.value = ''
  fetchData()
}

/* tabs */
const activeTab = ref('clinics')
const tabBtn = (which) =>
  ['px-4 py-2 rounded-lg text-sm', activeTab.value === which ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-50'].join(' ')

/* ========== clinic list built from actual stacionar data ========== */
const epicrisisIndex = computed(() => {
  const map = {}
  for (const e of allEpicrises.value) {
    const cid = e.clinic_id
    if (cid == null) continue
    if (!map[cid]) {
      map[cid] = {
        id: cid,
        clinic_id: cid,
        name: e.clinic_name || `Клиника ${cid}`,
        total: 0,
        by_text: 0,
        by_file: 0,
        doctors: new Set(),
      }
    } else if ((!map[cid].name || map[cid].name === `Клиника ${cid}`) && e.clinic_name) {
      map[cid].name = e.clinic_name
    }

    map[cid].total++
    if (e.input_type === 'text') map[cid].by_text++
    else map[cid].by_file++
    if (e.doctor_id) map[cid].doctors.add(e.doctor_id)

    if (e.doctor_id && e.clinic_name) map[`doctor:${e.doctor_id}`] = e.clinic_name
  }

  const indexedClinicRows = Object.values(map)
    .filter(v => v && typeof v === 'object' && 'total' in v)
    .map(c => ({ ...c, doctor_count: c.doctors.size }))
    .sort((a, b) => b.total - a.total)

  const hasAnalyticsClinicRows = Array.isArray(data.value?.by_clinic)
  const clinicRows = hasAnalyticsClinicRows
    ? data.value.by_clinic.map(c => ({
      id: c.clinic_id,
      clinic_id: c.clinic_id,
      name: c.clinic_name || `Клиника ${c.clinic_id}`,
      doctor_count: c.doctor_count ?? 0,
      by_text: c.by_text ?? 0,
      by_file: c.by_file ?? 0,
      total: c.total_epicrises ?? 0,
      last_activity: c.last_activity ?? null,
    }))
    : indexedClinicRows

  const clinicOptionsSource = indexedClinicRows.length ? indexedClinicRows : clinicRows
  const clinicOptions = clinicOptionsSource
    .map(c => ({ id: c.id, name: c.name }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const doctorClinicMap = {}
  for (const [key, value] of Object.entries(map)) {
    if (key.startsWith('doctor:')) {
      doctorClinicMap[key.replace('doctor:', '')] = value
    }
  }

  return { clinicRows, clinicOptions, doctorClinicMap }
})

const clinicOptions = computed(() => epicrisisIndex.value.clinicOptions)

const selectedClinicName = computed(() => {
  if (!clinicId.value) return ''
  const c = clinicOptions.value.find(x => String(x.id) === clinicId.value)
  return c?.name ?? `ID ${clinicId.value}`
})

/* clinic rows with breakdown */
const clinicRows = computed(() => epicrisisIndex.value.clinicRows)

/* ========== doctor → clinic name mapping ========== */
const doctorClinicMap = computed(() => epicrisisIndex.value.doctorClinicMap)

/* ========== API ========== */
async function fetchAllEpicrises({ force = false } = {}) {
  try {
    const cached = force ? null : readCachedValue(epicrisisCacheKey, EPICRISIS_CACHE_TTL)
    if (cached?.value) {
      allEpicrises.value = cached.value
      if (cached.fresh) return cached.value
    }

    const items = []
    let page = 1

    while (true) {
      const payload = await stacionarService.getAllEpicrises({ page, limit: epicrisisPageSize })
      const pageItems = extractEpicrisisItems(payload)
      items.push(...pageItems)

      const lastPage = resolveLastPage(payload, epicrisisPageSize, pageItems.length)
      if (lastPage != null) {
        if (page >= lastPage) break
      } else if (pageItems.length < epicrisisPageSize || pageItems.length === 0) {
        break
      }

      page += 1
      if (page > epicrisisMaxPages) break
    }

    allEpicrises.value = items
    writeCachedValue(epicrisisCacheKey, items)
    return items
  } catch (e) { /* no-op */ }
}

async function fetchData({ force = false } = {}) {
  error.value = null
  try {
    const params = getCurrentAnalyticsParams()

    const cacheKey = makeCacheKey('stacionar:analytics:v2', params)
    const cached = force ? null : readCachedValue(cacheKey, DASHBOARD_CACHE_TTL)

    if (cached?.value) {
      data.value = cached.value
      if (cached.fresh) return cached.value
    }

    loading.value = force || !cached?.value
    const result = await stacionarService.getAnalytics(params)
    data.value = result
    writeCachedValue(cacheKey, result)
    doctorPage.value = 1
    return result
  } catch (e) {
    error.value = e?.message || 'Ошибка загрузки'
  } finally {
    loading.value = false
  }
}

function getCurrentAnalyticsParams() {
  return normalizeParams({
    clinic_id: clinicId.value || undefined,
    period: period.value || undefined,
    from: !period.value ? (dateFrom.value || undefined) : undefined,
    to: !period.value ? (dateTo.value || undefined) : undefined,
  })
}

function resetDates() { dateFrom.value = ''; dateTo.value = ''; showCustomDates.value = false }
const exporting = ref(false)
const exportError = ref('')

/**
 * Выгрузка истории выбранной клиники в файл.
 *
 * Фильтры берём те же, что и у аналитики на экране, — выгрузка должна
 * совпадать с тем, что администратор сейчас видит. Без выбранной клиники
 * бэкенд отдаёт все, это допустимо для администратора.
 */
async function exportHistory() {
  exporting.value = true
  exportError.value = ''
  try {
    const { blob, filename } = await stacionarService.exportJson(getCurrentAnalyticsParams())
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch (e) {
    exportError.value = e?.message || 'Не удалось выгрузить'
  } finally {
    exporting.value = false
  }
}

function resetAll() { clinicId.value = ''; period.value = 'this_month'; resetDates(); activeTab.value = 'clinics'; fetchData() }
function clearClinicFilter() { clinicId.value = ''; fetchData(); activeTab.value = 'clinics' }

function handleClinicClick(row) {
  const id = row?.clinic_id ?? row?.id
  if (id == null) return
  clinicId.value = String(id)
  activeTab.value = 'doctors'
  fetchData()
}

function onClinicBarClick(params) {
  const row = clinicRows.value.find(r => r.name === params.name)
  if (row?.clinic_id != null || row?.id != null) handleClinicClick(row)
}

function refreshDashboard() {
  fetchAllEpicrises({ force: true })
  fetchData({ force: true })
}

onMounted(() => {
  fetchAllEpicrises()
  fetchData()
})

/* ========== doctors: search/sort/pagination ========== */
const doctorSearch = ref('')
const doctorSortBy = ref('total_epicrises')
const doctorSortDesc = ref(true)
const doctorPage = ref(1)
const doctorPageSize = ref(20)

const doctorsAll = computed(() => {
  if (!data.value) return []
  let rows = (data.value.by_doctor || []).map(d => ({
    ...d,
    _clinic_name: doctorClinicMap.value[d.doctor_id] || '—',
  }))
  const q = doctorSearch.value.trim().toLowerCase()
  if (q) {
    rows = rows.filter(d =>
      (d.doctor_name || '').toLowerCase().includes(q) ||
      (d._clinic_name || '').toLowerCase().includes(q)
    )
  }
  const key = doctorSortBy.value
  rows.sort((a, b) => {
    const av = a[key] ?? 0, bv = b[key] ?? 0
    if (key === 'last_activity') {
      const at = av ? new Date(av).getTime() : 0
      const bt = bv ? new Date(bv).getTime() : 0
      return doctorSortDesc.value ? bt - at : at - bt
    }
    if (typeof av === 'number' && typeof bv === 'number') return doctorSortDesc.value ? bv - av : av - bv
    return doctorSortDesc.value ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv))
  })
  return rows
})

const doctorTotalPages = computed(() => Math.max(1, Math.ceil(doctorsAll.value.length / doctorPageSize.value)))
const doctorPageStart = computed(() => (doctorPage.value - 1) * doctorPageSize.value)
const doctorPageEnd = computed(() => doctorPageStart.value + doctorPageSize.value)
const doctorsPaged = computed(() => doctorsAll.value.slice(doctorPageStart.value, doctorPageEnd.value))
watch([doctorSearch, doctorSortBy, doctorSortDesc, doctorPageSize], () => { doctorPage.value = 1 })

/* ========== doctor detail modal ========== */
const doctorDetail = ref(null)
const doctorDetailLoading = ref(false)

async function openDoctorDetail(d) {
  doctorDetailLoading.value = true
  doctorDetail.value = { doctor_name: d.doctor_name }
  try {
    doctorDetail.value = await stacionarService.getDoctorAnalytics(d.doctor_id, getCurrentAnalyticsParams())
  } catch (e) {
    doctorDetail.value = { ...d, _error: e?.message }
  } finally {
    doctorDetailLoading.value = false
  }
}

const complianceLabel = computed(() => {
  if (!doctorDetail.value?.compliance) return '—'
  const c = doctorDetail.value.compliance
  return `${c.high ?? 0} / ${c.medium ?? 0} / ${c.low ?? 0}`
})

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ru-RU', {
    timeZone: 'Asia/Almaty',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/* ========== charts ========== */
const simpleClinicChartOption = computed(() => {
  const rows = clinicRows.value.slice(0, 10)
  return {
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#0f172a',
      textStyle: { color: '#fff' },
    },
    grid: {
      top: '5%',
      left: '2%',
      right: '2%',
      bottom: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: rows.map(i => i.name),
      axisLabel: { color: '#94a3b8', fontSize: 10, rotate: 20 },
    },
    yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
    series: [
      {
        data: rows.map(i => i.total),
        type: 'bar',
        itemStyle: { color: '#0f172a', borderRadius: [6, 6, 0, 0] },
        barMaxWidth: 30,
      },
    ],
  }
})

const doctorsTop15 = computed(() => doctorsAll.value.slice(0, 15))
const doctorChartOption = computed(() => {
  const rows = doctorsTop15.value
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['По тексту', 'По файлу'] },
    grid: { left: 8, right: 8, top: 24, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: rows.map(r => r.doctor_name), axisLabel: { show: false } },
    yAxis: { type: 'value' },
    series: [
      { name: 'По тексту', type: 'bar', data: rows.map(r => r.by_text) },
      { name: 'По файлу', type: 'bar', data: rows.map(r => r.by_file) },
    ],
  }
})

const typeChartOption = computed(() => {
  const types = [
    { name: 'По тексту', value: data.value?.total_by_text ?? 0 },
    { name: 'По файлу', value: data.value?.total_by_file ?? 0 },
  ]
  return {
    tooltip: { trigger: 'item' },
    legend: { orient: 'horizontal', bottom: 0 },
    series: [{
      name: 'Тип ввода',
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: true,
      label: { show: true, formatter: '{b}: {c}' },
      data: types,
    }],
  }
})

const doctorDetailChart = computed(() => {
  const items = doctorDetail.value?.by_day || []
  return {
    tooltip: { trigger: 'axis' },
    grid: { left: 4, right: 4, top: 8, bottom: 4, containLabel: true },
    xAxis: { type: 'category', data: items.map(i => i.date), axisLabel: { fontSize: 10 } },
    yAxis: { type: 'value', minInterval: 1 },
    series: [{ type: 'bar', data: items.map(i => i.count), itemStyle: { borderRadius: [3, 3, 0, 0] } }],
  }
})
</script>

<style scoped>
/* same as AdminAnalytics — Tailwind handles everything */
</style>
